import { Box, Link, Stack } from '@mui/material';
import { visuallyHidden } from '@mui/utils';

import type { ReactNode } from 'react';

import type { WorkoutQuestion } from "../../api/workout-service";

import Markdown from "../markdown/Markdown";
import UserAnswers from "./UserAnswer";
import { FeedbackReport } from "./FeedbackReport";
import { RatingReport } from "./RatingReport";
import {
  QUIZ_BLUE,
  QUIZ_BORDER,
  QUIZ_INK,
  QUIZ_MUTED,
  QUIZ_PRIMARY_BUTTON,
  QUIZ_RIGHT,
  QUIZ_RIGHT_SOFT,
  QUIZ_SECONDARY_BUTTON,
  QUIZ_SURFACE,
  QUIZ_TEXT,
  QUIZ_WRONG,
  QUIZ_WRONG_SOFT
} from "./quiz-theme";

/** Markdown of questions, answers and hints: inline code as small chips, code blocks on their own panel. */
const MARKDOWN_SX = {
    '& p': { m: 0 },
    '& p + p, & p + pre, & pre + p': { mt: 1.5 },
    '& code': { fontFamily: "'JetBrains Mono', monospace", fontSize: '0.875em', bgcolor: '#EDF0F3', px: 0.625, py: '1px', borderRadius: '4px' },
    '& pre code, & pre div code': { bgcolor: 'transparent', p: 0 },
    // react-markdown's <pre> holds the highlighter's own box, which loses its margin to sit flush
    '& pre': { borderRadius: '12px', overflow: 'hidden', p: 0, my: 1 },
    '& pre > div': { m: '0 !important', borderRadius: 0 },
};

type OptionState = 'idle' | 'selected' | 'right' | 'wrong' | 'missed' | 'dimmed';

type OptionTag = { label: string; background: string; color: string };

const LOOKS: Record<OptionState, { border: string; line: string; background: string; color: string; tag?: OptionTag }> = {
    idle: { border: QUIZ_BORDER, line: 'solid', background: '#FFFFFF', color: QUIZ_INK },
    selected: { border: QUIZ_BLUE, line: 'solid', background: '#F3F7FE', color: QUIZ_INK },
    right: { border: QUIZ_RIGHT, line: 'solid', background: QUIZ_RIGHT_SOFT, color: QUIZ_INK, tag: { label: 'CORRECT', background: QUIZ_RIGHT, color: '#FFFFFF' } },
    wrong: { border: QUIZ_WRONG, line: 'solid', background: QUIZ_WRONG_SOFT, color: QUIZ_INK, tag: { label: 'WRONG PICK', background: QUIZ_WRONG, color: '#FFFFFF' } },
    missed: { border: QUIZ_RIGHT, line: 'dashed', background: '#FFFFFF', color: QUIZ_INK, tag: { label: 'RIGHT ANSWER', background: '#E9FCD4', color: '#08660D' } },
    dimmed: { border: QUIZ_BORDER, line: 'solid', background: '#FFFFFF', color: QUIZ_MUTED },
};

export type QuestionCardProps = {
    workoutId: string;
    question: WorkoutQuestion;
    answer: number;
    submitted: boolean;
    /** Lives are counted in verification sessions only, where a wrong answer costs one. */
    countsLives: boolean;
    onSelect: (index: number) => void;
    onSubmit: () => void;
    onSkip: () => void;
    onNext: () => void;
};

/** The question on screen: its kind, its text, the answers to pick from, and once answered, the verdict. */
export function QuestionCard(props: QuestionCardProps) {
    const { workoutId, question, answer, submitted, onSelect, onSubmit, onSkip, onNext } = props;

    return (
        <Stack component="section" spacing={ 2.5 } sx={ { ...QUIZ_SURFACE, width: '100%', maxWidth: 860, mx: 'auto', p: { xs: 2.5, sm: 3.5 }, color: QUIZ_INK } }>
            <Box component="span" sx={ {
                alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 12, fontWeight: 800,
                letterSpacing: '0.04em', px: 1.25, py: 0.625, borderRadius: '8px', bgcolor: '#E8F0FC', color: '#1A55B3',
            } }>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
                    <circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="3" fill="currentColor"/>
                </svg>
                PICK ONE
            </Box>

            <Box data-testid="question-text" sx={ { fontSize: 17, lineHeight: '26px', fontWeight: 600, ...MARKDOWN_SX } }>
                <Markdown text={ question.text }/>
            </Box>

            <Box component="fieldset" sx={ { border: 0, m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1.25 } }>
                <Box component="legend" sx={ visuallyHidden }>Answers</Box>
                { question.options.map((option, index) => (
                    <AnswerOption key={ `${ question.id }-${ index }` } name={ `answer-${ question.id }` } index={ index } text={ option }
                                  state={ optionState(index, answer, question.correct, submitted) } checked={ index === answer }
                                  disabled={ submitted } onSelect={ () => onSelect(index) }>
                        <UserAnswers question={ question } index={ index } show={ submitted }/>
                    </AnswerOption>
                )) }
            </Box>

            { submitted && <Verdict { ...props } correct={ answer === question.correct } skipped={ answer < 0 }/> }
            { submitted && (
                <Box sx={ { fontSize: 13, color: QUIZ_MUTED } }>
                    { answer === question.correct ? <RatingReport questionId={ question.id } workoutId={ workoutId }/>
                        : <FeedbackReport questionId={ question.id } workoutId={ workoutId }/> }
                </Box>
            ) }

            <Stack direction="row" spacing={ 1.5 } justifyContent="flex-end">
                { submitted ? (
                    <Box component="button" type="button" onClick={ onNext } sx={ QUIZ_PRIMARY_BUTTON }>Next question</Box>
                ) : <>
                    <Box component="button" type="button" onClick={ onSkip } sx={ QUIZ_SECONDARY_BUTTON }>Skip</Box>
                    <Box component="button" type="button" onClick={ onSubmit } disabled={ answer < 0 } sx={ QUIZ_PRIMARY_BUTTON }>Submit</Box>
                </> }
            </Stack>
        </Stack>
    );
}

function optionState(index: number, answer: number, correct: number, submitted: boolean): OptionState {
    if (!submitted) return index === answer ? 'selected' : 'idle';
    if (index === correct) return index === answer ? 'right' : 'missed';

    return index === answer ? 'wrong' : 'dimmed';
}

type AnswerOptionProps = {
    name: string;
    index: number;
    text: string;
    state: OptionState;
    checked: boolean;
    disabled: boolean;
    onSelect: () => void;
    children?: ReactNode;
};

/** One answer: a radio button with its text, marked right, wrong or missed once the answer is in. */
function AnswerOption({ name, index, text, state, checked, disabled, onSelect, children }: AnswerOptionProps) {
    const look = LOOKS[state];

    return (
        <Box component="label" data-testid="answer-option" sx={ {
            display: 'flex', gap: 1.75, alignItems: 'flex-start', px: 2, py: 1.75, borderRadius: '14px',
            border: `2px ${ look.line } ${ look.border }`, bgcolor: look.background, color: look.color, cursor: disabled ? 'default' : 'pointer',
            '&:hover': disabled || state === 'selected' ? undefined : { borderColor: '#CFD6DE' },
        } }>
            <Box component="input" type="radio" name={ name } value={ index } checked={ checked } disabled={ disabled } onChange={ onSelect }
                 sx={ { width: 22, height: 22, m: '1px 0 0', accentColor: QUIZ_BLUE, flexShrink: 0 } }/>

            <Stack spacing={ 0.75 } sx={ { flexGrow: 1, minWidth: 0, fontSize: 16, lineHeight: '24px', fontWeight: 600, ...MARKDOWN_SX } }>
                <Markdown text={ text }/>
                { children }
            </Stack>

            { look.tag && (
                <Box component="span" sx={ {
                    flexShrink: 0, fontSize: 11, fontWeight: 800, letterSpacing: '0.04em', px: 1, py: 0.5, borderRadius: '8px',
                    bgcolor: look.tag.background, color: look.tag.color,
                } }>
                    { look.tag.label }
                </Box>
            ) }
        </Box>
    );
}

type VerdictProps = QuestionCardProps & { correct: boolean; skipped: boolean };

/** How the answer went, with the explanation and where to read more. */
function Verdict({ question, countsLives, correct, skipped }: VerdictProps) {
    const title = correct ? 'Correct.' : skipped ? 'Skipped.' : 'Not quite.';
    const note = correct ? 'Well done, on to the next one.' : countsLives ? 'You lost a life.' : 'Read why before moving on.';

    return (
        <Stack role="status" spacing={ 1 } sx={ {
            px: 2, py: 1.75, borderRadius: '12px', fontSize: 14, lineHeight: '21px',
            bgcolor: correct ? QUIZ_RIGHT_SOFT : '#FFF8E1', color: correct ? '#0B4A10' : '#5C3D00',
        } }>
            <Box><b>{ title }</b> { note }</Box>
            { question.hint && <Box sx={ { color: QUIZ_TEXT, ...MARKDOWN_SX } }><Markdown text={ question.hint }/></Box> }
            { question.ref.length > 0 && (
                <Stack direction="row" flexWrap="wrap" columnGap={ 1.5 } sx={ { fontSize: 13 } }>
                    { question.ref.map((reference) => (
                        <Link key={ reference } href={ reference } target="_blank" rel="noreferrer" sx={ { wordBreak: 'break-all' } }>{ reference }</Link>
                    )) }
                </Stack>
            ) }
        </Stack>
    );
}
